/**
 * LeadScoringPage - Configure lead scoring rules
 * Displays scoring factors and allows recalculation
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Calculator, Info, Zap } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// Setting keys for lead scoring
const LEAD_SCORING_SETTINGS = {
  SCORING_ENABLED: 'lead_scoring_enabled',
  SCORE_EMAIL: 'lead_scoring_email',
  SCORE_PHONE: 'lead_scoring_phone',
  SCORE_HIGH_VALUE: 'lead_scoring_high_value',
  SCORE_HIGH_VALUE_THRESHOLD: 'lead_scoring_high_value_threshold',
  SCORE_MEDIUM_VALUE: 'lead_scoring_medium_value',
  SCORE_MEDIUM_VALUE_THRESHOLD: 'lead_scoring_medium_value_threshold',
  SCORE_VEHICLE_SELECTED: 'lead_scoring_vehicle_selected',
  SCORE_URGENT: 'lead_scoring_urgent',
  SCORE_URGENT_DAYS: 'lead_scoring_urgent_days',
  SCORE_HAS_DATE: 'lead_scoring_has_date',
  SCORE_REFERRAL: 'lead_scoring_referral',
  SCORE_WEBSITE: 'lead_scoring_website',
} as const

// Default scoring values (matches database function)
const DEFAULT_SCORES = {
  email: 15,
  phone: 15,
  highValue: 20,
  highValueThreshold: 200,
  mediumValue: 10,
  mediumValueThreshold: 100,
  vehicleSelected: 10,
  urgent: 15,
  urgentDays: 7,
  hasDate: 10,
  referral: 15,
  website: 10,
}

// Schema
const scoringSchema = z.object({
  scoringEnabled: z.boolean().default(true),
  scoreEmail: z.coerce.number().min(0).max(30),
  scorePhone: z.coerce.number().min(0).max(30),
  scoreHighValue: z.coerce.number().min(0).max(30),
  scoreHighValueThreshold: z.coerce.number().min(0),
  scoreMediumValue: z.coerce.number().min(0).max(30),
  scoreMediumValueThreshold: z.coerce.number().min(0),
  scoreVehicleSelected: z.coerce.number().min(0).max(30),
  scoreUrgent: z.coerce.number().min(0).max(30),
  scoreUrgentDays: z.coerce.number().min(1).max(30),
  scoreHasDate: z.coerce.number().min(0).max(30),
  scoreReferral: z.coerce.number().min(0).max(30),
  scoreWebsite: z.coerce.number().min(0).max(30),
})

type ScoringFormData = z.infer<typeof scoringSchema>

// Preview lead interface
interface PreviewLead {
  hasEmail: boolean
  hasPhone: boolean
  value: number
  hasVehicle: boolean
  pickupDaysFromNow: number
  source: 'website' | 'referral' | 'other'
}

function calculatePreviewScore(lead: PreviewLead, data: ScoringFormData): { score: number; breakdown: Record<string, number> } {
  let score = 0
  const breakdown: Record<string, number> = {}

  if (lead.hasEmail) {
    score += data.scoreEmail
    breakdown['Email provided'] = data.scoreEmail
  }

  if (lead.hasPhone) {
    score += data.scorePhone
    breakdown['Phone provided'] = data.scorePhone
  }

  if (lead.value >= data.scoreHighValueThreshold) {
    score += data.scoreHighValue
    breakdown['High value booking'] = data.scoreHighValue
  } else if (lead.value >= data.scoreMediumValueThreshold) {
    score += data.scoreMediumValue
    breakdown['Medium value booking'] = data.scoreMediumValue
  }

  if (lead.hasVehicle) {
    score += data.scoreVehicleSelected
    breakdown['Vehicle selected'] = data.scoreVehicleSelected
  }

  if (lead.pickupDaysFromNow <= data.scoreUrgentDays) {
    score += data.scoreUrgent
    breakdown['Urgent booking'] = data.scoreUrgent
  } else if (lead.pickupDaysFromNow > 0) {
    score += data.scoreHasDate
    breakdown['Has pickup date'] = data.scoreHasDate
  }

  if (lead.source === 'referral') {
    score += data.scoreReferral
    breakdown['Referral source'] = data.scoreReferral
  } else if (lead.source === 'website') {
    score += data.scoreWebsite
    breakdown['Website source'] = data.scoreWebsite
  }

  return { score: Math.min(score, 100), breakdown }
}

export function LeadScoringPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // State
  const [recalculateDialogOpen, setRecalculateDialogOpen] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)

  // Fetch account settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id, 'lead-scoring'],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)
        .like('key', 'lead_scoring_%')

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch lead count for recalculation dialog
  const { data: leadStats } = useQuery({
    queryKey: ['lead-count', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return { count: 0 }

      const { count, error } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', activeAccount.id)

      if (error) throw error
      return { count: count || 0 }
    },
    enabled: !!activeAccount?.id,
  })

  // Form
  const form = useForm<ScoringFormData>({
    resolver: zodResolver(scoringSchema),
    defaultValues: {
      scoringEnabled: true,
      scoreEmail: DEFAULT_SCORES.email,
      scorePhone: DEFAULT_SCORES.phone,
      scoreHighValue: DEFAULT_SCORES.highValue,
      scoreHighValueThreshold: DEFAULT_SCORES.highValueThreshold,
      scoreMediumValue: DEFAULT_SCORES.mediumValue,
      scoreMediumValueThreshold: DEFAULT_SCORES.mediumValueThreshold,
      scoreVehicleSelected: DEFAULT_SCORES.vehicleSelected,
      scoreUrgent: DEFAULT_SCORES.urgent,
      scoreUrgentDays: DEFAULT_SCORES.urgentDays,
      scoreHasDate: DEFAULT_SCORES.hasDate,
      scoreReferral: DEFAULT_SCORES.referral,
      scoreWebsite: DEFAULT_SCORES.website,
    },
  })

  // Watch form values for preview
  const formValues = form.watch()

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        scoringEnabled: settings[LEAD_SCORING_SETTINGS.SCORING_ENABLED] !== 'false',
        scoreEmail: settings[LEAD_SCORING_SETTINGS.SCORE_EMAIL]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_EMAIL])
          : DEFAULT_SCORES.email,
        scorePhone: settings[LEAD_SCORING_SETTINGS.SCORE_PHONE]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_PHONE])
          : DEFAULT_SCORES.phone,
        scoreHighValue: settings[LEAD_SCORING_SETTINGS.SCORE_HIGH_VALUE]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_HIGH_VALUE])
          : DEFAULT_SCORES.highValue,
        scoreHighValueThreshold: settings[LEAD_SCORING_SETTINGS.SCORE_HIGH_VALUE_THRESHOLD]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_HIGH_VALUE_THRESHOLD])
          : DEFAULT_SCORES.highValueThreshold,
        scoreMediumValue: settings[LEAD_SCORING_SETTINGS.SCORE_MEDIUM_VALUE]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_MEDIUM_VALUE])
          : DEFAULT_SCORES.mediumValue,
        scoreMediumValueThreshold: settings[LEAD_SCORING_SETTINGS.SCORE_MEDIUM_VALUE_THRESHOLD]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_MEDIUM_VALUE_THRESHOLD])
          : DEFAULT_SCORES.mediumValueThreshold,
        scoreVehicleSelected: settings[LEAD_SCORING_SETTINGS.SCORE_VEHICLE_SELECTED]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_VEHICLE_SELECTED])
          : DEFAULT_SCORES.vehicleSelected,
        scoreUrgent: settings[LEAD_SCORING_SETTINGS.SCORE_URGENT]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_URGENT])
          : DEFAULT_SCORES.urgent,
        scoreUrgentDays: settings[LEAD_SCORING_SETTINGS.SCORE_URGENT_DAYS]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_URGENT_DAYS])
          : DEFAULT_SCORES.urgentDays,
        scoreHasDate: settings[LEAD_SCORING_SETTINGS.SCORE_HAS_DATE]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_HAS_DATE])
          : DEFAULT_SCORES.hasDate,
        scoreReferral: settings[LEAD_SCORING_SETTINGS.SCORE_REFERRAL]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_REFERRAL])
          : DEFAULT_SCORES.referral,
        scoreWebsite: settings[LEAD_SCORING_SETTINGS.SCORE_WEBSITE]
          ? Number(settings[LEAD_SCORING_SETTINGS.SCORE_WEBSITE])
          : DEFAULT_SCORES.website,
      })
    }
  }, [settings, form])

  // Save settings mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ScoringFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORING_ENABLED,
          value: String(data.scoringEnabled),
          description: 'Enable lead scoring',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_EMAIL,
          value: String(data.scoreEmail),
          description: 'Points for having email',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_PHONE,
          value: String(data.scorePhone),
          description: 'Points for having phone',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_HIGH_VALUE,
          value: String(data.scoreHighValue),
          description: 'Points for high value booking',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_HIGH_VALUE_THRESHOLD,
          value: String(data.scoreHighValueThreshold),
          description: 'High value threshold',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_MEDIUM_VALUE,
          value: String(data.scoreMediumValue),
          description: 'Points for medium value booking',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_MEDIUM_VALUE_THRESHOLD,
          value: String(data.scoreMediumValueThreshold),
          description: 'Medium value threshold',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_VEHICLE_SELECTED,
          value: String(data.scoreVehicleSelected),
          description: 'Points for selecting a vehicle',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_URGENT,
          value: String(data.scoreUrgent),
          description: 'Points for urgent booking',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_URGENT_DAYS,
          value: String(data.scoreUrgentDays),
          description: 'Days threshold for urgent',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_HAS_DATE,
          value: String(data.scoreHasDate),
          description: 'Points for having a date',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_REFERRAL,
          value: String(data.scoreReferral),
          description: 'Points for referral source',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LEAD_SCORING_SETTINGS.SCORE_WEBSITE,
          value: String(data.scoreWebsite),
          description: 'Points for website source',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id, 'lead-scoring'] })
      toast({
        title: 'Scoring rules saved',
        description: 'Your lead scoring settings have been updated. New leads will use these rules.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Recalculate scores for all leads
  const handleRecalculateScores = async () => {
    if (!activeAccount?.id) return

    setIsRecalculating(true)
    try {
      // Fetch all leads for this account
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select(`
          id,
          customer_email,
          customer_phone,
          estimated_value,
          source,
          pickup_datetime,
          quote_id,
          booking_quotes (
            vehicle_id,
            total_amount
          )
        `)
        .eq('account_id', activeAccount.id)

      if (leadsError) throw leadsError

      if (!leads || leads.length === 0) {
        toast({
          title: 'No leads to recalculate',
          description: 'There are no leads in your account.',
        })
        setRecalculateDialogOpen(false)
        setIsRecalculating(false)
        return
      }

      let updatedCount = 0
      const now = new Date()

      // Process each lead
      for (const lead of leads) {
        const hasEmail = !!lead.customer_email
        const hasPhone = !!lead.customer_phone
        const value = lead.estimated_value || (lead.booking_quotes as any)?.total_amount || 0
        const hasVehicle = !!(lead.booking_quotes as any)?.vehicle_id

        let pickupDaysFromNow = 999
        if (lead.pickup_datetime) {
          const pickupDate = new Date(lead.pickup_datetime)
          pickupDaysFromNow = Math.ceil((pickupDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        }

        const previewLead: PreviewLead = {
          hasEmail,
          hasPhone,
          value,
          hasVehicle,
          pickupDaysFromNow: pickupDaysFromNow > 0 ? pickupDaysFromNow : 999,
          source: lead.source === 'referral' ? 'referral' : lead.source === 'website' ? 'website' : 'other',
        }

        const { score, breakdown } = calculatePreviewScore(previewLead, formValues)

        // Update the lead
        const { error: updateError } = await supabase
          .from('leads')
          .update({
            score,
            score_breakdown: breakdown,
          })
          .eq('id', lead.id)

        if (!updateError) {
          updatedCount++
        }
      }

      toast({
        title: 'Scores recalculated',
        description: `Updated ${updatedCount} lead${updatedCount !== 1 ? 's' : ''} with new scores.`,
      })

      // Invalidate lead queries
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to recalculate scores',
        variant: 'destructive',
      })
    } finally {
      setIsRecalculating(false)
      setRecalculateDialogOpen(false)
    }
  }

  // Calculate preview scores for sample leads
  const sampleLeads: PreviewLead[] = [
    { hasEmail: true, hasPhone: true, value: 300, hasVehicle: true, pickupDaysFromNow: 3, source: 'referral' },
    { hasEmail: true, hasPhone: false, value: 150, hasVehicle: true, pickupDaysFromNow: 14, source: 'website' },
    { hasEmail: true, hasPhone: true, value: 80, hasVehicle: false, pickupDaysFromNow: 999, source: 'other' },
  ]

  const previewScores = sampleLeads.map(lead => calculatePreviewScore(lead, formValues))

  const scoringEnabled = form.watch('scoringEnabled')

  if (settingsLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => saveMutation.mutate(data))}
          className="space-y-6"
        >
          {/* Enable/Disable Scoring */}
          <Card>
            <CardHeader>
              <CardTitle>Lead Scoring</CardTitle>
              <CardDescription>
                Automatically score leads based on their engagement and potential value
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="scoringEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable Lead Scoring</FormLabel>
                      <FormDescription>
                        Calculate scores for new leads automatically
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {scoringEnabled && (
            <>
              {/* Contact Information Scores */}
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>
                    Points awarded for providing contact details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="scoreEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Provided</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">points</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scorePhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Provided</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">points</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Booking Value Scores */}
              <Card>
                <CardHeader>
                  <CardTitle>Booking Value</CardTitle>
                  <CardDescription>
                    Points based on estimated booking value
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="scoreHighValue"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>High Value Booking</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">points</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scoreHighValueThreshold"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>High Value Threshold</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">$</span>
                              <Input
                                type="number"
                                min={0}
                                step={10}
                                className="w-24"
                                {...field}
                              />
                              <span className="text-muted-foreground">or more</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Separator />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="scoreMediumValue"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Medium Value Booking</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">points</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scoreMediumValueThreshold"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Medium Value Threshold</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">$</span>
                              <Input
                                type="number"
                                min={0}
                                step={10}
                                className="w-24"
                                {...field}
                              />
                              <span className="text-muted-foreground">or more</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Engagement Scores */}
              <Card>
                <CardHeader>
                  <CardTitle>Engagement Level</CardTitle>
                  <CardDescription>
                    Points based on how engaged the lead is
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="scoreVehicleSelected"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Vehicle Selected</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={30}
                              className="w-20"
                              {...field}
                            />
                            <span className="text-muted-foreground">points</span>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Awarded when the lead has selected a specific vehicle
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Timing Scores */}
              <Card>
                <CardHeader>
                  <CardTitle>Timing</CardTitle>
                  <CardDescription>
                    Points based on booking urgency
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="scoreUrgent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Urgent Booking</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">points</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scoreUrgentDays"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Urgent Within</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={1}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">days</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Separator />
                  <FormField
                    control={form.control}
                    name="scoreHasDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Has Pickup Date (not urgent)</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={30}
                              className="w-20"
                              {...field}
                            />
                            <span className="text-muted-foreground">points</span>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Awarded when pickup date is set but beyond the urgent threshold
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Source Scores */}
              <Card>
                <CardHeader>
                  <CardTitle>Lead Source</CardTitle>
                  <CardDescription>
                    Points based on how the lead was acquired
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="scoreReferral"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Referral</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">points</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scoreWebsite"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Website</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={30}
                                className="w-20"
                                {...field}
                              />
                              <span className="text-muted-foreground">points</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Score Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Score Preview
                  </CardTitle>
                  <CardDescription>
                    See how different lead profiles would score with your current rules
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Hot Lead Example */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-500 hover:bg-red-500">Hot Lead</Badge>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-sm">
                                  Email + Phone + $300 value + Vehicle selected + Pickup in 3 days + Referral source
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <span className="text-2xl font-bold">{previewScores[0].score}</span>
                      </div>
                      <Progress value={previewScores[0].score} className="h-2" />
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(previewScores[0].breakdown).map(([key, value]) => (
                          <Badge key={key} variant="outline" className="text-xs">
                            {key}: +{value}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Warm Lead Example */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-orange-500 hover:bg-orange-500">Warm Lead</Badge>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-sm">
                                  Email only + $150 value + Vehicle selected + Pickup in 14 days + Website source
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <span className="text-2xl font-bold">{previewScores[1].score}</span>
                      </div>
                      <Progress value={previewScores[1].score} className="h-2" />
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(previewScores[1].breakdown).map(([key, value]) => (
                          <Badge key={key} variant="outline" className="text-xs">
                            {key}: +{value}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Cold Lead Example */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">Cold Lead</Badge>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-sm">
                                  Email + Phone + $80 value + No vehicle + No date + Other source
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <span className="text-2xl font-bold">{previewScores[2].score}</span>
                      </div>
                      <Progress value={previewScores[2].score} className="h-2" />
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(previewScores[2].breakdown).map(([key, value]) => (
                          <Badge key={key} variant="outline" className="text-xs">
                            {key}: +{value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>

            {scoringEnabled && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setRecalculateDialogOpen(true)}
              >
                <Zap className="mr-2 h-4 w-4" />
                Recalculate All Scores
              </Button>
            )}
          </div>
        </form>
      </Form>

      {/* Recalculate Confirmation Dialog */}
      <AlertDialog open={recalculateDialogOpen} onOpenChange={setRecalculateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate Lead Scores</AlertDialogTitle>
            <AlertDialogDescription>
              This will recalculate scores for all {leadStats?.count || 0} lead{(leadStats?.count || 0) !== 1 ? 's' : ''} using your current scoring rules.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRecalculating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRecalculateScores}
              disabled={isRecalculating}
            >
              {isRecalculating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recalculating...
                </>
              ) : (
                'Recalculate Scores'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
